import { Button, Card, CardContent } from '@/ui/design-system/components';
import { dimensions, timeseries } from '@/copy';
import { parsePath } from 'history';
import { useCurrentCountry, useEndDate, useGoogleData } from 'utils/hooks';
import { BodySemi } from './Text';
import CategoryDescriptionCard from './CategoryDescriptionCard';
import DimensionIcon from './DimensionIcon';
import DownloadTimeSeriesButton from './Button/DownloadTimeSeriesButton';
import DrawerBand from './DrawerBand';
import DrawerHeader from './DrawerHeader';
import { buildExportFilename } from 'utils/formatCSV';
import { FiExternalLink } from 'react-icons/fi';
import GoogleTimeSeries from './DataVis/Charts/GoogleTimeSeries/GoogleTimeSeries';
import { LinkTracked } from './Link';
import { NoAccessMessageMarkets } from './NoAccessMessage';
import { DATA_SOURCES, SINGULAR_NAMES } from '@/config/constants';
import { Skeleton } from '@/ui/design-system/components/ui/skeleton';
import Stack from './Stack';
import ToggleWatch from 'components/Button/ToggleWatch';
import { TopKeywordsDrawerSection } from './TopKeywordsSection';
import { cn } from '@/ui/design-system/utils/cn';
import { getParentIdByAncestorHint } from 'utils/hooks/useGetHierarchy';
import { getPathFromNode } from 'utils/helpers';
import { getSubcategoryLabel } from 'utils/hierarchies';
import styled from 'styled-components';
import { useAffinityCategoryLinks } from 'utils/hooks/api';
import { useFetchUserAbout } from 'utils/hooks/user';
import { useLocation } from 'react-router';
import { useRef } from 'react';
import { v5 as uuidv5 } from 'uuid';
import MetricHeroCard from 'components/MetricHeroCard';

const CHART_HEIGHT = 287;

const namesList = Object.entries(SINGULAR_NAMES);
const mapTypeToDomain = (type) => {
    const [domain] =
        namesList.find(([_, singular]) => type === `${singular}Id`) || [];
    return domain;
};

const DrawerSection = styled.div`
    padding: 0 1rem;
`;

function DrawerContent({
    paginateBy,
    controller,
    parentHierarchies,
    skipPrediction = false,
    showAddToWatchlist = true,
    showParentContext = true,
    source = 'pop-index',
    week: _ignoredWeek,
    year: _ignoredYear,
    ...itemIds
}) {
    const userAboutQuery = useFetchUserAbout();
    const { pathname } = useLocation();
    const { countryId } = itemIds;
    const { [paginateBy]: paginateId, ...relatedIds } = itemIds;
    const relatedIdEntries = Object.entries(relatedIds);
    const [relatedIdKey, relatedId] = relatedIdEntries.length
        ? relatedIdEntries[0]
        : [];
    const paginateDomain = mapTypeToDomain(paginateBy);
    const relatedDomain = mapTypeToDomain(relatedIdKey);
    const country = useCurrentCountry();
    const date = useEndDate(source);
    const itemData =
        paginateId && countryId
            ? {
                  [paginateDomain]: [paginateId],
                  countries: [countryId],
              }
            : null;

    if (itemData && relatedId) {
        itemData[relatedDomain] = [relatedId];
    }

    const { data: items, isLoading: isLoadingItems } = useGoogleData({
        enabled: !!itemData,
        data: itemData,
        paginateBy: relatedDomain || paginateDomain,
        controller,
    });
    const { data: relatedItems, isLoading: isLoadingRelatedItems } =
        useGoogleData({
            enabled: !!(paginateId && countryId),
            data: {
                [paginateDomain]: paginateId ? [paginateId] : null,
                countries: countryId ? [countryId] : null,
            },
            paginateBy: paginateDomain,
        });

    const [item] = items || [];
    const [relatedItem] = relatedItems || [];

    const { name, time_series, id: itemId, type: itemType } = item || {};

    const {
        name: relatedName,
        type: relatedType,
        id: relatedItemId,
        hierarchies: relatedItemHierarchies,
    } = relatedItem || {};

    const linkedCategoriesEnabled =
        !!(relatedItemId && countryId) && relatedType === 'affinity';

    const linkedCategoriesQuery = useAffinityCategoryLinks({
        affinities: relatedItemId ? [relatedItemId] : null,
        countries: countryId ? [countryId] : null,
        enabled: linkedCategoriesEnabled,
    });

    const timeSeriesRef = useRef();

    if (
        isLoadingItems ||
        isLoadingRelatedItems ||
        !item ||
        item.fetching ||
        !relatedItem ||
        relatedItem.fetching
    ) {
        return <DrawerContentSkeleton />;
    }

    let relatedItemTo = null;

    if (relatedType === 'geo') {
        relatedItemTo = `${pathname}?country=${relatedItem.text_id}`;
    } else if (
        relatedType === 'affinity' &&
        relatedName &&
        linkedCategoriesQuery.data?.[0]?.category_id
    ) {
        relatedItemTo = getPathFromNode({
            depth: 'cat5',
            type: 'category',
            name: relatedName,
            id: linkedCategoriesQuery.data?.[0]?.category_id,
        });
    } else if (relatedItem && !['affinity', 'product'].includes(relatedType)) {
        relatedItemTo = getPathFromNode(
            relatedItem,
            getParentIdByAncestorHint(
                relatedItemHierarchies,
                parentHierarchies?.[0]?.[1],
            ),
        );
    }

    let timeSeriesName = itemType === 'geo' ? '' : name;
    if (itemId !== relatedItemId && relatedType !== 'geo')
        timeSeriesName
            ? (timeSeriesName += ` + ${relatedName}`)
            : (timeSeriesName = relatedName);
    if (country) timeSeriesName += ` (${country.code || country.name})`;

    if (
        relatedType === 'geo' &&
        userAboutQuery.data &&
        relatedItem &&
        !userAboutQuery.data.allowed_countries?.includes(relatedItem.text_id)
    ) {
        return <NoAccessMessageMarkets />;
    }

    let watchlistItemData = {};
    if (relatedType === 'category' || relatedType === 'brand') {
        watchlistItemData = {
            name: relatedName,
            type: relatedType,
        };
    } else if (relatedType === 'affinity') {
        const affinityOfCategoryOrBrand = itemIds.categoryId
            ? 'categoryId'
            : 'brandId';
        watchlistItemData = {
            name: relatedItem.name,
            type: 'affinity',
            [affinityOfCategoryOrBrand]: itemIds[affinityOfCategoryOrBrand],
            affinityId: relatedItem.id,
        };
    }

    // Google's drawer serves both an explore row and a related row. One context
    // slot, two sources: the parent entity when the row sits under one, the
    // subcategory otherwise. An explore row paginates by country, so a geo
    // `item` is the market being viewed, not a parent worth naming — and on the
    // Google explore tables the other id is the category filter the page was
    // opened with, which says nothing about the row, so those call sites pass
    // `showParentContext={false}`.
    const isRelatedRow =
        showParentContext &&
        itemId !== relatedItemId &&
        relatedType !== 'geo' &&
        itemType !== 'geo';
    const contextLabel = isRelatedRow
        ? name
        : getSubcategoryLabel(
              relatedItemHierarchies,
              parentHierarchies ? { hierarchies: parentHierarchies } : null,
          );

    const relatedHealthStats = relatedItem?.healthStats;
    // No related-entity drawer on any source shows a hero. TikTok's and Instagram's
    // rows carry aggregates only; Google's numbers describe the entity rather than
    // the pairing, and the related tables print no health-stat labels, so a hero
    // here reads as a column the table is withholding.
    const hasHero =
        !isRelatedRow &&
        !!(
            relatedHealthStats ||
            relatedItem?.YoY != null ||
            relatedItem?.cluster
        );

    return (
        <Stack $space="1.5rem" css="padding-bottom: 2.25rem;">
            <DrawerHeader
                name={relatedName}
                eyebrow={
                    <>
                        <DimensionIcon dimensionType={relatedType} />
                        {relatedType ? dimensions[relatedType].singular : null}
                    </>
                }
                contextLabel={contextLabel}
                contextPrefix={isRelatedRow ? 'in' : undefined}
            >
                {showAddToWatchlist && relatedItem && (
                    <ToggleWatch
                        id={
                            relatedType === 'affinity'
                                ? uuidv5(
                                      itemIds.categoryId || itemIds.brandId,
                                      relatedItem.id,
                                  )
                                : relatedItem.id
                        }
                        data={watchlistItemData}
                        btnChildren="Add to watchlist"
                    />
                )}
                {relatedItemTo ? (
                    <Button variant="default" size="sm" asChild>
                        <LinkTracked
                            to={{
                                // parsePath so a query lands in `search`, not glued
                                // to `pathname` — react-router only splits a `to` it
                                // is given as a string.
                                ...parsePath(relatedItemTo),
                                ...(['category', 'brand'].includes(itemType) &&
                                    relatedType === 'affinity' &&
                                    linkedCategoriesQuery.data?.[0]
                                        ?.is_hidden_root &&
                                    item && {
                                        state: { backToItem: item },
                                    }),
                            }}
                            mixpanelContext="DataTableDrawer"
                            mixpanelMeta={{ itemNames: [name, relatedName] }}
                        >
                            See more data
                            <FiExternalLink />
                        </LinkTracked>
                    </Button>
                ) : linkedCategoriesQuery.isLoading ? (
                    <Button variant="default" size="sm" disabled>
                        See more data
                        <FiExternalLink />
                    </Button>
                ) : null}
            </DrawerHeader>
            <DrawerBand>
                {hasHero && (
                    <Card className="overflow-visible">
                        <MetricHeroCard
                            source={DATA_SOURCES.GOOGLE}
                            data={relatedItem}
                            healthStats={relatedHealthStats}
                            cluster={relatedItem?.cluster}
                        />
                    </Card>
                )}
                <Card>
                    <CardContent className="p-4 flex flex-col gap-3">
                        <div className="flex items-center justify-between">
                            <BodySemi as="span">Searches</BodySemi>
                            <DownloadTimeSeriesButton
                                menuAlign="right"
                                csvFilename={buildExportFilename({
                                    entity: (() => {
                                        let entityParts =
                                            itemType === 'geo' ? '' : name;
                                        if (
                                            itemId !== relatedItemId &&
                                            relatedType !== 'geo'
                                        ) {
                                            entityParts = entityParts
                                                ? `${entityParts} ${relatedName}`
                                                : relatedName;
                                        }
                                        return entityParts;
                                    })(),
                                    country,
                                    source,
                                    kind: 'time-series',
                                    date,
                                })}
                                imageTitle={timeSeriesName}
                                data={
                                    time_series
                                        ? [
                                              {
                                                  name: timeSeriesName,
                                                  timeSeries: time_series,
                                              },
                                          ]
                                        : null
                                }
                                wrapperRef={timeSeriesRef}
                            />
                        </div>
                        <div ref={timeSeriesRef}>
                            <GoogleTimeSeries
                                data={[
                                    {
                                        id: 'drawer-time-series',
                                        timeSeries: time_series,
                                    },
                                ]}
                                showPlaceholder={!time_series?.length}
                                placeholderMessage={timeseries.empty}
                                showPlaceholderBackground={false}
                                children={null}
                                chartHeight={CHART_HEIGHT}
                                name={relatedName}
                                skipPrediction={skipPrediction}
                            />
                        </div>
                    </CardContent>
                </Card>
                {['category', 'affinity'].includes(relatedType) ? (
                    <CategoryDescriptionCard
                        categoryId={
                            relatedType === 'affinity' &&
                            linkedCategoriesQuery.data?.[0]?.category_id
                                ? linkedCategoriesQuery.data?.[0]?.category_id
                                : relatedType === 'category'
                                  ? relatedItemId
                                  : null
                        }
                        categoryName={relatedName}
                        flavors={relatedItem.flavors}
                    />
                ) : null}
            </DrawerBand>
            <DrawerSection>
                <TopKeywordsDrawerSection
                    enabled={!!itemData}
                    data={itemData}
                    paginateBy={paginateDomain}
                    name={relatedName || null}
                    controller={controller}
                    filename={buildExportFilename({
                        entity: relatedName || name,
                        country,
                        source,
                        type: 'search-query',
                        kind: 'data',
                        date,
                    })}
                    displayQty={10}
                    showIcon={false}
                />
            </DrawerSection>
        </Stack>
    );
}

const S = ({ className, ...props }) => (
    <Skeleton className={cn('bg-neutral-200', className)} {...props} />
);

function DrawerContentSkeleton() {
    return (
        <div className="flex flex-col gap-6 pb-9">
            <div className="px-4 pt-5 flex flex-col gap-2">
                <S className="w-28 h-3.5" />
                <S className="w-40 h-6" />
                <S className="w-24 h-3" />
            </div>
            <div className="bg-muted px-4 py-4 flex flex-col gap-3">
                <S className="w-full h-[60px] rounded-lg" />
                <S
                    className="w-full rounded-lg"
                    style={{ height: CHART_HEIGHT + 56 }}
                />
            </div>
            <div className="px-4 flex flex-col gap-3">
                <S className="w-36 h-4" />
                <S className="w-[70%] h-6 rounded-lg" />
                <S className="w-[55%] h-6 rounded-lg" />
                <S className="w-[62%] h-6 rounded-lg" />
            </div>
        </div>
    );
}

export default DrawerContent;
